import React from "react";
import { ColumnApi, GetRowIdParams, GridApi, GridOptions, GridReadyEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { List, Set } from "immutable";
import "./target-metrics-styles.css";
import { ISchemaKey } from "helpers/product-collection";
import { TargetGroupWithTargets } from "components/product-collection/footer/types";
import { useTargets } from "./hooks";
import { useFilteredRowData } from "./hooks/useFilteredRowData";
import { useColumns } from "./hooks/useColumns";
import { getSchemaFieldByDetailView } from "./helpers";
import productCollectionGridViewStyles from "../product-collection-grid-view/product-collection-grid-view-styles";
import { ColumnTypes } from "../product-collection-grid-view/product-collection-grid-view-helper";
import { AllocationBag } from "helpers/allocation-dimension";
import { IMAGE_URL, AG_GRID_SCROLLABLE_EL_SELECTOR } from "constants/product-collection";
import { DisplayedGridRows } from "reducers/collection-target/types";
import { useGridRowData } from "./hooks/useGridRowData";
import { ProductCollectionGridView } from "../product-collection-grid-view";
import { MetricsState } from "reducers/dimensions-panel/dimensions-panel";
import { $, merge } from "glamor";

interface Props {
    connectedGrid: {
        ref: React.RefObject<AgGridReact>;
        gridViewRef: React.RefObject<ProductCollectionGridView>;
        gridApi: GridApi;
        columnApi: ColumnApi;
        columnsDefs: GridOptions["columnDefs"];
    };
    toggleDoors: (force: true) => void;
    showDoors: boolean;
    schema: List<ISchemaKey>;
    merchantId: string;
    targetGroupsWithTargets: TargetGroupWithTargets[];
    targetBag: AllocationBag;
    selectedTargetIds: Set<string>;
    displayedRows: DisplayedGridRows;
    metrics: MetricsState;
    isCollapseModeOn: boolean;
}

export const FinancialTargetGrid = React.forwardRef<AgGridReact, Props>((props: Props, ref) => {
    const {
        toggleDoors,
        showDoors,
        targetGroupsWithTargets,
        targetBag,
        connectedGrid,
        schema,
        merchantId,
        selectedTargetIds,
        displayedRows,
        isCollapseModeOn,
        metrics,
    } = props;
    const mainGridScrollLeft = React.useRef(0);
    const rowsPerTarget = displayedRows && Object.values(displayedRows)?.filter((value) => value)?.length;
    const columnTypes = React.useMemo(
        () => ({
            [ColumnTypes.AllocationDimensionTotal]: {},
            [ColumnTypes.ClassificationTotal]: {},
            [ColumnTypes.DoorBulkQuantity]: {},
            [ColumnTypes.AllocationDimension]: {},
            [ColumnTypes.AllDimensionTotal]: {},
            [ColumnTypes.NestedDimensionTotal]: {},
            [ColumnTypes.DateColumn]: {},
            [ColumnTypes.Metrics]: {},
        }),
        []
    );

    const jsSchema = React.useMemo(() => schema?.toJS() ?? [], [schema]);

    const [financialTargetGridApi, setFinancialTargetGridApi] = React.useState<GridApi>();

    const titleFromSchema = getSchemaFieldByDetailView("is_title", jsSchema);
    const mappedTargets = useTargets(targetBag, targetGroupsWithTargets, selectedTargetIds);
    const mainGridCols = connectedGrid.gridApi.getColumnDefs();
    const rowData = useGridRowData({
        schema: jsSchema,
        columnDefs: mainGridCols,
        merchantId,
        displayedRows,
        targets: mappedTargets,
    });
    const filteredRowDataByTargetId = useFilteredRowData(
        mappedTargets,
        connectedGrid.gridApi,
        /*
        IMPORTANT: This is required to to listen for column modifications on main grid.
        Relying on `connectedGrid.gridApi.getColumnDefs` won't work because it doesn't rerender.

        This hook is generating `filteredRowDataByTargetId` which will tell `useColumns` to generate new columns.
        */
        connectedGrid.columnsDefs
    );

    const columnDefs = useColumns(
        filteredRowDataByTargetId,
        connectedGrid.gridApi,
        connectedGrid.gridViewRef,
        mappedTargets,
        displayedRows,
        isCollapseModeOn,
        metrics
    );

    const maybePinTitleColumn = () => {
        const columns = connectedGrid.columnApi.getAllGridColumns();
        // Check if there is existing pinned columns other than image column. If nothing, use title field
        const leftPinnedCount = columns.reduce((acc, column) => {
            if (column.isPinnedLeft() && column.getColId() !== IMAGE_URL) {
                acc++;
            }
            return acc;
        }, 0);

        if (leftPinnedCount === 0) {
            connectedGrid.columnApi.setColumnPinned(titleFromSchema.field_name, "left");
            connectedGrid.columnApi.setColumnWidth(titleFromSchema.field_name, 300);
        }
    };

    const maybeTurnDimensionsOn = () => {
        if (!showDoors) {
            // There is no way around this since we need to have columns pinned before turning on Doors. Else it will reset any pinned columns
            process.nextTick(() => {
                toggleDoors(true);
            });
        }
    };

    const onGridReady = ({ api }: GridReadyEvent) => {
        setFinancialTargetGridApi(api);
        matchGridsScrolls();
        maybePinTitleColumn();
        maybeTurnDimensionsOn();
    };

    const getRowId = React.useMemo(() => {
        return (params: GetRowIdParams) => params.data.id;
    }, []);

    // Function to scroll target grid the same amount as the main grid on mount
    const matchGridsScrolls = () => {
        const scrollableEls = document.querySelectorAll(AG_GRID_SCROLLABLE_EL_SELECTOR);
        const targetMetricsGridScrollableEl = scrollableEls[1];

        // We need the timeout, otherwise target grid api will scroll both grids
        // to the initial position "left: 0"
        setTimeout(() => {
            targetMetricsGridScrollableEl?.scrollTo({ left: mainGridScrollLeft.current });
        }, 150);
    };

    React.useEffect(() => {
        // this is just to refresh the grid and update the reference for the aligned
        // grid, otherwise it will maintain the old reference and break the grid
        // TODO revisit this after upgrading ag-grid to newer versions
        connectedGrid.gridApi.addItems(null);
    }, [connectedGrid.gridApi]);

    // Storing the main grid amount of horizontal scroll to replicate in target grid
    React.useEffect(() => {
        const scrollableEls = document.querySelectorAll(AG_GRID_SCROLLABLE_EL_SELECTOR);
        const mainGridScrollableEl = scrollableEls[0];
        const mainGridScrollX = mainGridScrollableEl?.scrollLeft ?? 0;
        // We store the value in a ref because we need it before target grid
        // moves the scroll of the main grid, which will reset to x = 0
        // then we use it after the grid is ready (onGridReady) to scroll both back
        // to the correct position
        mainGridScrollLeft.current = mainGridScrollX;
    }, []);

    const styles = React.useMemo(() => {
        return merge(
            $(".ag-theme-balham .target-plan-grid .ag-row:nth-child(" + rowsPerTarget + "n)", {
                borderBottom: "1px solid #BDBDBD !important",
            })
        );
    }, [rowsPerTarget]);
    const mergedStyles = { ...productCollectionGridViewStyles(), ...styles };

    return (
        <div className="product-collection ag-theme-balham" {...mergedStyles}>
            <AgGridReact
                className="target-plan-grid"
                ref={ref}
                alignedGrids={connectedGrid.ref.current ? [connectedGrid.ref.current] : null}
                rowData={rowData}
                columnDefs={columnDefs}
                suppressRowTransform
                onGridReady={onGridReady}
                getRowId={getRowId}
                rowBuffer={50}
                valueCache
                columnTypes={columnTypes}
                onRowDataChanged={({ api }) => {
                    // Using to redraw rows whenever we change displayedRows
                    // otherwise ag-grid won't recalculate the rowSpan prop
                    api.redrawRows();
                }}
                enableCellChangeFlash
            />
        </div>
    );
});
